<template>
  <sfs-base-list-filter
    title="Filter Revenue"
    :table-name="tableName"
    :applied-filters="appliedFilters"
    :displayed-columns="displayedColumns"
    :available-columns="availableColumns"
    :column-picker="false"
    @filters-applied="onFiltersApplied"
    @filters-reset="onDefaultPresetApplied"
    @filter-cleared="onFilterCleared"
    :advanced-filter="false"
    :popover-filter="true"
  >
    <template #quick-filters>
      <b-form-group
        label="Date Bucket"
        label-for="quick-filter-revenue_dates"
        label-sr-only
      >
        <div class="d-flex">
          <sfs-date-range-picker
            id="quick-filter-revenue_dates"
            :value="datesRange"
            placeholder="Select Dates"
            @on-change="onDatesChange"
          />
          <b-input-group-append>
            <b-button
              id="quick-filter-apply-button"
              @click="applyFilters"
              variant="primary"
            >
              <i class="fa fa-search" />
            </b-button>
          </b-input-group-append>
        </div>
      </b-form-group>
    </template>

    <template #popover-filters>
      <b-row>
        <b-col>
          <b-form-group
            label="Client"
            label-for="advanced-filter-client_id"
            label-cols-lg="5"
            content-cols-lg="7"
          >
            <client-select
              id="advanced-filter-client_id"
              placeholder="All"
              :value="client"
              @input="onClientChange"
              :persist-state="true"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-form-group
            label="Service"
            label-for="advanced-filter-service_id"
            label-cols-lg="5"
            content-cols-lg="7"
          >
            <service-select
              id="advanced-filter-service_id"
              placeholder="All"
              :value="service"
              @input="onServiceChange"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-form-group
            label="Service Category"
            label-for="advanced-filter-service_category"
            label-cols-lg="5"
            content-cols-lg="7"
          >
            <service-category-select
              id="advanced-filter-service_category"
              placeholder="All"
              v-model="filters.service_category"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-form-group
            label="Manager"
            label-for="advanced-filter-manager_id"
            label-cols-lg="5"
            content-cols-lg="7"
          >
            <employee-select
              id="advanced-filter-manager_id"
              placeholder="All"
              :value="manager"
              @input="onManagerChange"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-form-group
            label="State"
            label-for="advanced-filter-state"
            label-cols-lg="5"
            content-cols-lg="7"
          >
            <state-select
              id="advanced-filter-state"
              v-model="filters.state"
              placeholder="All"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </template>
    <template #right-tray>
      <sfs-download-button
        v-b-tooltip.hover
        :provider-params="filters"
        filename="Revenue Report.csv"
        :file-provider="$analyticsAPI.getWorkOrdersRevenueReportSpreadsheet"
        title="Download spreadsheet"
      />
    </template>
  </sfs-base-list-filter>
</template>
<script>
import ServiceSelect from '@/components/admin/services/Select.vue';
import ServiceCategorySelect from '@/components/admin/services/CategorySelect.vue';
import ClientSelect from '@/components/admin/clients/Select.vue';
import EmployeeSelect from '@/components/admin/users/EmployeeSelect.vue';
import StateSelect from '@/components/admin/locations/StateSelect.vue';
import listFilterMixin from '@/mixins/listFilterMixin';
import { revenueReportDateFilterStringToJsDate } from '@/utils/revenueReportDefaultFiscalYearDates';
import {
  WORK_ORDER_REVENUE_REPORT_TABLE_NAME,
  WORK_ORDER_REVENUE_REPORT_TABLE_COLUMN_OPTIONS,
  WORK_ORDER_REVENUE_REPORT_TABLE_DEFAULT_COLUMNS,
}
from '@/constants/reports';

export default {
  name: 'RevenueListFilter',
  mixins: [listFilterMixin],
  components: {
    ServiceSelect,
    ServiceCategorySelect,
    ClientSelect,
    EmployeeSelect,
    StateSelect,
  },
  data() {
    return {
      tableName: WORK_ORDER_REVENUE_REPORT_TABLE_NAME,
      availableColumns: WORK_ORDER_REVENUE_REPORT_TABLE_COLUMN_OPTIONS,
    };
  },
  mounted() {
    this.loadInitialFilters();
  },
  methods: {
    applyPresetFilter(filters, columns) {
      this.filters = filters;
      this.displayedColumns = columns;
      this.applyFilters();
    },
    onDefaultPresetApplied() {
      this.applyPresetFilter({}, WORK_ORDER_REVENUE_REPORT_TABLE_DEFAULT_COLUMNS);
    },
    onServiceChange(service) {
      this.filters.service_id = service ? service.id.toString() : '';
      this.service = service;
    },
    onClientChange(client) {
      this.filters.client_id = client ? client.id.toString() : '';
      this.client = client;
    },
    onManagerChange(user) {
      this.filters.manager_id = user ? user.id.toString() : '';
      this.manager = user;
    },
    onDatesChange(dates) {
      if (dates[0] && dates[1]) {
        this.filters.start_date = dates[0] ? this.$options.filters.dateFormat(dates[0].toISOString()) : '';
        this.filters.end_date = dates[1] ? this.$options.filters.dateFormat(dates[1].toISOString()) : '';
        this.applyFilters();
      }
    },
    loadInitialFilters() {
      const loadClient = new Promise((resolve) => {
        if (this.initialFilters.client_id) {
          this.$clientsAPI.get(this.initialFilters.client_id).then((client) => {
            resolve(client);
          });
        } else {
          resolve(null);
        }
      });

      const loadService = new Promise((resolve) => {
        if (this.initialFilters.service_id) {
          this.$servicesAPI.get(this.initialFilters.service_id).then((service) => {
            resolve(service);
          });
        } else {
          resolve(null);
        }
      });

      const loadManager = new Promise((resolve) => {
        if (this.initialFilters.manager_id) {
          this.$usersAPI.get(this.initialFilters.manager_id).then((manager) => {
            resolve(manager);
          });
        } else {
          resolve(null);
        }
      });
      // The order of the resolved promises is preserved so we are able to use
      // indexes to fetch the data.
      Promise.all([loadClient, loadService, loadManager]).then((values) => {
        this.client = values[0];
        this.service = values[1];
        this.manager = values[2];
        this.applyFilters();
      });
    },
  },
  computed: {
    datesRange() {
      return [revenueReportDateFilterStringToJsDate(this.filters.start_date), revenueReportDateFilterStringToJsDate(this.filters.end_date)];
    },
  },
};
</script>
